/**
 * Document Fields Service
 * Handles API calls for document field detection and management
 * JIRA: KOPS-206 - Frontend Integration
 */

import axiosInstance from '../helpers/api';

export interface DetectedField {
  id: string;
  type: 'signature' | 'date' | 'text' | 'initial' | 'checkbox';
  label: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  source: 'textract' | 'gpt_vision' | 'manual';
  required?: boolean;
  mapping?: string;
}

export interface FieldMappingResponse {
  success: boolean;
  hasFieldMappings: boolean;
  documentId: string;
  documentName: string;
  documentType: string;
  fields: DetectedField[];
  fieldCount: number;
  detectionMethod: string;
  confidence: number;
  fieldBreakdown: {
    signature: number;
    text: number;
    date: number;
    checkbox: number;
    initial: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DetectionTriggerResponse {
  success: boolean;
  message: string;
  alreadyDetected?: boolean;
  fieldCount?: number;
  fields?: DetectedField[];
  confidence?: number;
  fieldBreakdown?: {
    signature: number;
    text: number;
    date: number;
    checkbox: number;
    initial: number;
  };
}

export interface BrokerageTemplate {
  id: string;
  name: string;
  type: string;
  description: string;
  fieldCount: number;
  signatureCount: number;
  detectionMethod: string;
  confidence: number;
  mappedAt: string;
  hasFillableTemplate: boolean;
  pdffillerTemplateId?: string;
  pdffillerFillableId?: string;
  pdffillerEditUrl?: string;
  pdffillerStatus?: string | null;
}

class DocumentFieldsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get field mappings for a document
   */
  async getFieldMappings(documentId: string): Promise<FieldMappingResponse> {
    try {
      const response = await axiosInstance.get(
        `document-fields/${documentId}`,
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching field mappings:', error);
      throw new Error('Failed to fetch field mappings');
    }
  }

  /**
   * Trigger field detection for a document
   */
  async detectFields(documentId: string, forceRedetect = false): Promise<DetectionTriggerResponse> {
    try {
      const response = await axiosInstance.post(
        `document-fields/${documentId}/detect`,
        { forceRedetect },
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error detecting fields:', error);
      throw new Error('Failed to detect fields');
    }
  }

  /**
   * Update field mappings (manual adjustments)
   */
  async updateFieldMappings(documentId: string, fields: DetectedField[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axiosInstance.put(
        `document-fields/${documentId}`,
        { fields },
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating field mappings:', error);
      throw new Error('Failed to update field mappings');
    }
  }

  /**
   * Delete field mappings
   */
  async deleteFieldMappings(documentId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axiosInstance.delete(
        `document-fields/${documentId}`,
        { headers: this.getAuthHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error deleting field mappings:', error);
      throw new Error('Failed to delete field mappings');
    }
  }

  /**
   * Get field-mapped templates for a brokerage
   */
  async getBrokerageTemplates(brokerageId: string, documentType?: string): Promise<{ 
    success: boolean; 
    templates: BrokerageTemplate[]; 
    totalTemplates: number; 
  }> {
    try {
      let url = `document-fields/brokerage/${brokerageId}/templates`;
      if (documentType) {
        url += `?documentType=${encodeURIComponent(documentType)}`;
      }
      
      const response = await axiosInstance.get(url, { headers: this.getAuthHeaders() });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching brokerage templates:', error);
      throw new Error('Failed to fetch templates');
    }
  }

  /**
   * Check if a document has field detection status
   */
  async checkFieldDetectionStatus(documentId: string): Promise<{
    hasFields: boolean;
    isProcessing: boolean;
    error?: string;
  }> {
    try {
      const mappings = await this.getFieldMappings(documentId);
      
      return {
        hasFields: mappings.hasFieldMappings,
        isProcessing: false
      };
    } catch (error) {
      // If document not found or no mappings, it might be processing
      return {
        hasFields: false,
        isProcessing: true
      };
    }
  }

  /**
   * Utility: Convert field coordinates to CSS styles
   */
  convertFieldToCSS(field: DetectedField, containerWidth: number = 100, containerHeight: number = 100) {
    return {
      position: 'absolute' as const,
      left: `${field.x}%`,
      top: `${field.y}%`,
      width: `${field.width}%`,
      height: `${field.height}%`,
      border: `2px ${field.type === 'signature' ? 'dashed' : 'solid'} ${this.getFieldColor(field.type)}`,
      backgroundColor: `${this.getFieldColor(field.type)}1A`, // 10% opacity
      borderRadius: '4px',
      zIndex: 10
    };
  }

  /**
   * Get color for field type
   */
  getFieldColor(fieldType: DetectedField['type']): string {
    const colors = {
      signature: '#a855f7', // Purple
      date: '#22c55e',      // Green
      text: '#3b82f6',      // Blue
      initial: '#fb923c',   // Orange
      checkbox: '#ef4444'   // Red
    };
    
    return colors[fieldType] || '#6b7280';
  }

  /**
   * Get field type icon
   */
  getFieldIcon(fieldType: DetectedField['type']): string {
    const icons = {
      signature: '✍️',
      date: '📅',
      text: '✏️',
      initial: '🔤',
      checkbox: '☐'
    };
    
    return icons[fieldType] || '📄';
  }

  /**
   * Format confidence score for display
   */
  formatConfidence(confidence: number): string {
    return `${Math.round(confidence * 100)}%`;
  }

  /**
   * Get field type display name
   */
  getFieldTypeName(fieldType: DetectedField['type']): string {
    const names = {
      signature: 'Signature',
      date: 'Date',
      text: 'Text Field',
      initial: 'Initials',
      checkbox: 'Checkbox'
    };
    
    return names[fieldType] || 'Field';
  }
}

export default new DocumentFieldsService();
